import React, { useState } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Zoom,
  Paper,
  Alert,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Dice3D } from '../components/Dice3D.tsx';
import { useThemeContext } from '../contexts/ThemeContext.tsx';

// --- STYLED COMPONENTS (shouldForwardProp-pal a hiba ellen) ---

interface StyledProps {
  darkMode?: boolean;
}

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    background: darkMode 
      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
      : 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    color: darkMode ? 'white' : '#1a1a2e',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
    padding: theme.spacing(1),
  },
}));

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  borderRadius: '12px',
  padding: '10px 24px',
  fontWeight: 'bold',
  textTransform: 'none',
  background: darkMode 
    ? 'linear-gradient(45deg, #00d2ff, #3a7bd5)' 
    : theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    background: darkMode 
      ? 'linear-gradient(45deg, #3a7bd5, #00d2ff)' 
      : theme.palette.primary.dark,
  }
}));

const ResultPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  borderRadius: '16px',
  textAlign: 'center',
  background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
  border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
}));

// --- MAIN COMPONENT ---

const Randomizer: React.FC = () => {
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isAuthenticated = !!Cookies.get('token');

  // Basic random state
  const [basicResult, setBasicResult] = useState<number | null>(null);

  // Custom dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [customMin, setCustomMin] = useState<string>('0');
  const [customMax, setCustomMax] = useState<string>('100');
  const [customResult, setCustomResult] = useState<number | null>(null);

  // Dice results
  const [leftDiceResult, setLeftDiceResult] = useState<number | null>(null);
  const [rightDiceResult, setRightDiceResult] = useState<number | null>(null);

  // Error dialog state
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');

  const generateBasicRandom = () => {
    const randomNum = Math.floor(Math.random() * 99) + 1;
    setBasicResult(randomNum);
  };

  const handleOpenDialog = () => {
    setCustomResult(null);
    setOpenDialog(true);
  };

  const handleCustomGenerate = () => {
    const min = parseInt(customMin, 10);
    const max = parseInt(customMax, 10);
    if (isNaN(min) || isNaN(max) || min > max) {
      setErrorDialogMessage('Invalid interval: the minimum cannot be greater than the maximum!');
      setErrorDialogOpen(true);
      return;
    }
    const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
    setCustomResult(randomNum);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        overflow: 'hidden'
      }}
    >
      {/* Background Particles */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <Particles />
      </Box>

      {/* Navigation */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
      </Box>

      {/* Main content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 4,
          mt: 10,
          gap: 4
        }}
      >
        {/* Left Dice Column */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', opacity: 0.8 }}>
            Left Dice Roll
          </Typography>
          <Dice3D onRollComplete={(face) => setLeftDiceResult(face)} />
          <Zoom in={leftDiceResult !== null}>
            <ResultPaper darkMode={darkMode}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#00d2ff' }}>
                Result: {leftDiceResult}
              </Typography>
            </ResultPaper>
          </Zoom>
        </Box>

        {/* Center Main Controls */}
        <Box sx={{ 
          flex: 1.5, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
          p: 4,
          borderRadius: '24px',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
        }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center', background: 'linear-gradient(45deg, #00d2ff, #3a7bd5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Randomizer
          </Typography>

          {/* Basic Random */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
              Quick Roll (1–100)
            </Typography>
            <ActionButton darkMode={darkMode} onClick={generateBasicRandom}>
              Generate Number
            </ActionButton>
            <Zoom in={basicResult !== null}>
              <Typography variant="h2" sx={{ mt: 2, fontWeight: 'bold', color: darkMode ? '#00d2ff' : 'primary.main' }}>
                {basicResult}
              </Typography>
            </Zoom>
          </Box>

          <Box sx={{ width: '80%', height: '1px', bgcolor: 'divider', mb: 5 }} />

          {/* Custom Launcher */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
              Need custom range?
            </Typography>
            <Button 
              variant="outlined" 
              onClick={handleOpenDialog}
              sx={{ 
                borderRadius: '12px', 
                borderWidth: '2px', 
                fontWeight: 'bold',
                px: 4,
                '&:hover': { borderWidth: '2px' }
              }}
            >
              Open Customizer
            </Button>
          </Box>
        </Box>

        {/* Right Dice Column */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', opacity: 0.8 }}>
            Right Dice Roll
          </Typography>
          <Dice3D onRollComplete={(face) => setRightDiceResult(face)} />
          <Zoom in={rightDiceResult !== null}>
            <ResultPaper darkMode={darkMode}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3a7bd5' }}>
                Result: {rightDiceResult}
              </Typography>
            </ResultPaper>
          </Zoom>
        </Box>
      </Box>

      {/* Custom Randomizer Dialog */}
      <AnimatedDialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        TransitionComponent={Zoom}
        darkMode={darkMode}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem' }}>
          Custom Settings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label="Minimum"
              type="number"
              fullWidth
              value={customMin}
              onChange={(e) => setCustomMin(e.target.value)}
              variant="filled"
              InputProps={{ disableUnderline: true, sx: { borderRadius: '12px' } }}
            />
            <TextField
              label="Maximum"
              type="number"
              fullWidth
              value={customMax}
              onChange={(e) => setCustomMax(e.target.value)}
              variant="filled"
              InputProps={{ disableUnderline: true, sx: { borderRadius: '12px' } }}
            />
          </Box>

          <Zoom in={customResult !== null}>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>Generated Result:</Typography>
              <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#00d2ff' }}>
                {customResult}
              </Typography>
            </Box>
          </Zoom>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: darkMode ? 'white' : 'text.primary' }}>
            Cancel
          </Button>
          <ActionButton darkMode={darkMode} onClick={handleCustomGenerate}>
            Generate
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>

      {/* Error Dialog */}
      <AnimatedDialog 
        open={errorDialogOpen} 
        onClose={() => setErrorDialogOpen(false)}
        TransitionComponent={Zoom}
        darkMode={darkMode}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Range Error</DialogTitle>
        <DialogContent>
          <Alert severity="error" variant="filled" sx={{ borderRadius: '12px' }}>
            {errorDialogMessage}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            fullWidth 
            variant="contained" 
            onClick={() => setErrorDialogOpen(false)}
            sx={{ borderRadius: '10px', bgcolor: '#ff4b2b' }}
          >
            I'll fix it
          </Button>
        </DialogActions>
      </AnimatedDialog>
    </Box>
  );
};

export default Randomizer;